import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CoArchitectLogo, AlertCircle } from '../components/Icons';

type Step = 'email' | 'otp' | 'done';

function ForgotPassword() {
  const navigate = useNavigate();
  const { sendPasswordResetOTP, resetPassword } = useAuth();

  const [step, setStep]             = useState<Step>('email');
  const [email, setEmail]           = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [otp, setOtp]               = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [showPass, setShowPass]     = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError]     = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const canSubmitEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const otpFilled      = otp.every(d => d !== '');
  const canSubmitReset = otpFilled && newPassword.length >= 8;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown(v => v - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setEmailLoading(true);
    try {
      await sendPasswordResetOTP(email.trim().toLowerCase());
      setStep('otp');
      setResendCooldown(30);
    } catch {
      setEmailError('Could not send reset code. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  // ── OTP input handling ───────────────────────────────────────────────────

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next  = [...otp];
    next[index] = digit;
    setOtp(next);
    if (otpError) setOtpError('');
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      setOtp(text.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setOtpError('');
    try {
      await sendPasswordResetOTP(email.trim().toLowerCase());
      setOtp(['', '', '', '', '', '']);
      setResendCooldown(30);
      otpRefs.current[0]?.focus();
    } catch {
      setOtpError('Could not resend. Please try again.');
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    setOtpLoading(true);
    try {
      await resetPassword(email.trim().toLowerCase(), otp.join(''), newPassword);
      setStep('done');
    } catch (err: any) {
      const msg: string = err.response?.data?.message ?? err.response?.data?.error ?? '';
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid')) {
        setOtpError('Code is invalid or expired — if you signed up with Google, use "Sign in with Google" instead.');
      } else {
        setOtpError('Something went wrong. Please try again.');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'otp') setStep('email');
    else navigate('/login');
  };

  return (
    <div className="auth-page">
      <div style={{ width: '100%', maxWidth: 420 }}>
        <button className="auth-back-btn" onClick={handleBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          {step === 'otp' ? 'Back' : 'Back to Login'}
        </button>
      </div>

      <div className="auth-card">
        <div className="auth-brand">
          <div className="auth-brand-logo">
            <CoArchitectLogo size={80} />
          </div>
          <h1>Reset Password</h1>
          <p>
            {step === 'email' && 'Enter your email to receive a reset code'}
            {step === 'otp'   && `Enter the code sent to ${email}`}
            {step === 'done'  && 'Password updated successfully'}
          </p>
        </div>

        {step === 'email' && (
          <form onSubmit={handleEmailSubmit} className="auth-form" noValidate>
            <div className="form-field">
              <label className="form-label" htmlFor="fp-email">Email</label>
              <input
                id="fp-email"
                className="form-input"
                type="email"
                autoComplete="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                autoFocus
                required
              />
            </div>

            {emailError && (
              <span className="form-error" role="alert">
                <AlertCircle size={13} />{emailError}
              </span>
            )}

            <button type="submit" className="btn btn-primary" disabled={emailLoading || !canSubmitEmail}>
              {emailLoading
                ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Sending code…</>
                : 'Send Reset Code'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleReset} className="auth-form" noValidate>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-faint)', textAlign: 'center', marginBottom: 8 }}>
              Check your inbox and enter the 6-digit code.
            </p>

            <div className="otp-container" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { otpRefs.current[i] = el; }}
                  className={`otp-input${otpError ? ' error' : ''}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  autoFocus={i === 0}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            <div className="form-field" style={{ marginTop: 8 }}>
              <label className="form-label" htmlFor="fp-newpass">New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="fp-newpass"
                  className="form-input"
                  type={showPass ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Min. 8 characters"
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); if (otpError) setOtpError(''); }}
                  style={{ paddingRight: 40 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                           background: 'none', border: 'none', cursor: 'pointer',
                           color: 'var(--text-faint)', fontSize: 12, padding: 4 }}
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                >
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {otpError && (
              <span className="form-error" role="alert">
                <AlertCircle size={13} />{otpError}
              </span>
            )}

            <button type="submit" className="btn btn-primary" disabled={otpLoading || !canSubmitReset}>
              {otpLoading
                ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Updating password…</>
                : 'Set New Password'}
            </button>

            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              style={{ fontSize: '0.8125rem' }}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="auth-form" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <p style={{ color: 'var(--text-faint)', fontSize: '0.9rem', marginBottom: 20 }}>
              Your password has been updated. Sign in with your new password.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/login', { replace: true })}
            >
              Go to Sign In
            </button>
          </div>
        )}

        <div className="auth-footer">
          Remember your password?{' '}
          <Link to="/login" className="auth-link">Sign in</Link>
        </div>
      </div>

      <div className="auth-social-footer">
        <a href="https://www.instagram.com/coarchitect.app" target="_blank" rel="noopener noreferrer" className="auth-social-row">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5"/>
            <circle cx="12" cy="12" r="4"/>
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
          </svg>
          @coarchitect.app
        </a>
        <div className="auth-legal-row">
          <Link to="/privacy" className="auth-legal-link">Privacy Policy</Link>
          <span className="auth-legal-sep" />
          <Link to="/terms" className="auth-legal-link">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
